// src/components/MathRenderer.tsx
/**
 * MathRenderer Component
 * 
 * Renders mathematical expressions using KaTeX for LaTeX formatting.
 * Handles both simple text and complex mathematical expressions.
 * Uses UK mathematical conventions (no dots for implicit multiplication).
 */

import React from 'react';
import 'katex/dist/katex.min.css';
import katex from 'katex';

interface MathRendererProps {
  expression: string;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Convert mathematical expression to LaTeX format using UK conventions
 */
function convertToLatex(expression: string): string {
  if (!expression || typeof expression !== 'string') {
    return expression;
  }

  // Remove extra whitespace
  let latex = expression.trim();

  // If the expression already contains LaTeX commands (like \frac, \times, etc.), 
  // return it as-is to avoid breaking the LaTeX
  const hasLatexCommands = /\\[a-zA-Z]+/.test(latex);
  
  if (hasLatexCommands) {
    // For expressions that already contain LaTeX, return it as-is
    // Don't apply any transformations that might break LaTeX commands
    // Just normalize whitespace
    return latex.replace(/\s+/g, ' ').trim();
  } else {
    // For plain expressions, apply all transformations using UK conventions
    // Handle fractions first (before any other transformations)
    latex = latex.replace(/([a-zA-Z])\/(\d+)/g, '\\frac{$1}{$2}');
    latex = latex.replace(/(\d+)\/([a-zA-Z])/g, '\\frac{$1}{$2}');
    latex = latex.replace(/([a-zA-Z])\/([a-zA-Z])/g, '\\frac{$1}{$2}');
    latex = latex.replace(/(\d+)\/(\d+)/g, '\\frac{$1}{$2}');

    // Handle implicit multiplication patterns using UK conventions (no dots)
    // Use a more careful approach to avoid interfering with LaTeX commands
    const parts = latex.split(/(\\[a-zA-Z]+\{[^}]*\})/);
    
    const processedParts = parts.map((part) => {
      // Skip LaTeX commands
      if (part.match(/^\\[a-zA-Z]+\{[^}]*\}$/)) {
        return part;
      }
      
      // Process non-LaTeX parts - be more careful to avoid LaTeX commands
      let processed = part;
      
      // Only apply transformations if the part doesn't contain LaTeX commands
      if (!/\\[a-zA-Z]+/.test(processed)) {
        // Apply transformations one by one, checking for LaTeX after each
        // UK convention: implicit multiplication without dots
        processed = processed.replace(/(\d+)([a-zA-Z])/g, '$1$2');  // 2a -> 2a (no dot)
        
        // Check if we created LaTeX commands
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/([a-zA-Z])(\d+)/g, '$1$2');  // a2 -> a2 (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/([a-zA-Z])([a-zA-Z])/g, '$1$2');  // ab -> ab (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/(\d+)\(/g, '$1(');  // 2(3) -> 2(3) (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/([a-zA-Z])\(/g, '$1(');  // a(3) -> a(3) (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/\)(\d+)/g, ')$1');  // (2)3 -> (2)3 (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/\)([a-zA-Z])/g, ')$1');  // (2)a -> (2)a (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
        
        processed = processed.replace(/\)\(/g, ')(');  // (2)(3) -> (2)(3) (no dot)
        if (/\\[a-zA-Z]+/.test(processed)) {
          return processed;
        }
      }
      
      return processed;
    });
    
    latex = processedParts.join('');

    // Handle explicit operators
    latex = latex
      .replace(/\*/g, '\\cdot ')  // Keep explicit multiplication with dot
      .replace(/\^(\d+)/g, '^{$1}')
      .replace(/\^([a-zA-Z])/g, '^{$1}')
      .replace(/\^\(([^)]+)\)/g, '^{($1)}')
      .replace(/\(/g, '(')
      .replace(/\)/g, ')');
  }

  // Handle special cases
  latex = latex
    .replace(/\s+/g, ' ')  // Normalize spaces
    .trim();

  return latex;
}

const MathRenderer: React.FC<MathRendererProps> = ({ 
  expression, 
  style = {}, 
  className = '' 
}) => {
  // Use useMemo for synchronous rendering - prevents flash during panning
  const renderedHtml = React.useMemo(() => {
    // Convert expression to LaTeX format
    const latexExpression = convertToLatex(expression);

    // Use KaTeX directly
    try {
      const html = katex.renderToString(latexExpression, {
        throwOnError: false,
        displayMode: false,
        output: 'html',
        strict: false
      });
      return html;
    } catch (error) {
      console.warn('KaTeX rendering error:', error, 'for expression:', latexExpression);
      return expression; // Fallback to plain text
    }
  }, [expression]);

  // Always use dangerouslySetInnerHTML for KaTeX rendering
  return (
    <span 
      style={style} 
      className={className}
      dangerouslySetInnerHTML={{ __html: renderedHtml }}
    />
  );
};

export default MathRenderer; 